import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const QRCodeModal = ({ isOpen, onClose, user }) => {
  const [qrCode, setQrCode] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      // Generate QR code data (in real app, this would be a proper QR code)
      const qrData = `https://zentridms.company.com/invite?token=${user?.id}-${Date.now()}`;
      setQrCode(qrData);
    }
  }, [isOpen, user]);

  if (!isOpen || !user) return null;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard?.writeText(qrCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleDownloadQR = () => {
    // In a real app, this would generate and download an actual QR code image
    const element = document.createElement('a');
    const file = new Blob([`QR Code for ${user.name}\nInvite Link: ${qrCode}`], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `qr-code-${user?.name?.replace(/\s+/g, '-')?.toLowerCase()}.txt`;
    document.body?.appendChild(element);
    element?.click();
    document.body?.removeChild(element);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-300 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="QrCode" size={20} color="white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-card-foreground">QR Code Access</h2>
              <p className="text-sm text-muted-foreground">Quick setup for {user?.name}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
            iconSize={20}
          >
            <span className="sr-only">Close</span>
          </Button>
        </div>

        <div className="p-6 space-y-6">
          {/* QR Code Display */}
          <div className="flex flex-col items-center space-y-4">
            <div className="w-48 h-48 bg-muted border-2 border-dashed border-border rounded-lg flex items-center justify-center">
              <div className="text-center">
                <Icon name="QrCode" size={64} className="text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">QR Code</p>
                <p className="text-xs text-muted-foreground">Scan to access</p>
              </div>
            </div>
            
            <div className="text-center">
              <p className="text-sm font-medium text-foreground mb-1">Scan with mobile device</p>
              <p className="text-xs text-muted-foreground">
                Opens ZentriDMS login with pre-filled credentials
              </p>
            </div>
          </div>

          {/* Invite Link */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
              Direct Invite Link
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={qrCode}
                readOnly
                className="flex-1 px-3 py-2 border border-border rounded-md bg-muted text-foreground text-sm"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyLink}
                iconName={copied ? "Check" : "Copy"}
                iconSize={16}
              >
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-muted/50 border border-border rounded-lg p-4">
            <h4 className="font-medium text-foreground mb-2 flex items-center gap-2">
              <Icon name="Info" size={16} />
              Setup Instructions
            </h4>
            <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
              <li>Share QR code or link with the user</li>
              <li>User scans code or clicks link on mobile device</li>
              <li>ZentriDMS app opens with pre-configured access</li>
              <li>User completes profile setup and password creation</li>
            </ol>
          </div>

          {/* Security Notice */}
          <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Shield" size={16} className="text-warning flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-warning text-sm mb-1">Security Notice</h4>
                <p className="text-xs text-warning/80">
                  This QR code contains a secure token that expires in 24 hours. 
                  Share only with the intended user through secure channels.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/30">
          <Button
            variant="outline"
            onClick={handleDownloadQR}
            iconName="Download"
            iconPosition="left"
            size="sm"
          >
            Download QR
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Close
            </Button>
            <Button
              onClick={handleCopyLink}
              iconName="Share"
              iconPosition="left"
            >
              Share Link
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRCodeModal;